import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { siteSections } from "@/data/siteMap";

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// tiny markdown-lite renderer (h2, paragraphs, ordered lists, bold **)
function renderBody(body) {
  if (!body) return null;
  const blocks = body.split(/\n\n+/);
  return blocks.map((b, i) => {
    if (b.startsWith("## ")) {
      return (
        <h2 key={i} className="font-display text-2xl font-bold tracking-tight text-[#F5F5F7] mt-10 mb-4">
          {b.replace(/^##\s*/, "")}
        </h2>
      );
    }
    if (/^\d+\.\s/.test(b)) {
      const items = b.split("\n").filter(Boolean).map((line) => line.replace(/^\d+\.\s*/, ""));
      return (
        <ol key={i} className="list-decimal list-inside space-y-2 text-[#C5C6C7] my-6 marker:text-[#FF3355] marker:font-mono">
          {items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: it.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#F5F5F7]">$1</strong>') }} />
          ))}
        </ol>
      );
    }
    return (
      <p key={i} className="text-[#C5C6C7] leading-relaxed my-4"
        dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#F5F5F7]">$1</strong>').replace(/`([^`]+)`/g, '<code class="font-mono text-[#FF3355] text-sm bg-[#1F2833] px-1.5 py-0.5">$1</code>') }} />
    );
  });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/insights/${slug}`);
        setPost(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (notFound) return <Navigate to="/insights" replace />;
  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 font-mono text-sm text-[#C5C6C7]">&gt; loading<span className="te-caret">_</span></div>;
  if (!post) return null;

  const categoryLabel = siteSections.insights.items.find((c) => c.slug === post.category)?.label || post.category;

  return (
    <article className="bg-[#0B0C10]" data-testid={`insight-detail-${post.slug}`}>
      <section className="border-b border-[#1F2833] py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/insights" data-testid="insight-back" className="font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7] hover:text-[#FF3355] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> all insights
          </Link>
          <div className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// {categoryLabel}</div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">{post.title}</h1>
          <div className="mt-6 font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7]">
            {post.author} · {formatDate(post.published_at)}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {renderBody(post.body)}
        </div>
      </section>

      <section className="py-16 border-t border-[#1F2833]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border border-[#1F2833] bg-[#0d1117] p-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-2">// NEXT.STEP</div>
            <h3 className="font-display text-xl font-bold">Scope an engagement with the founders.</h3>
          </div>
          <Link to="/terminal" data-testid="insight-cta" className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]">
            Initialize <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </article>
  );
}
